// src/components/demo/DemoRiderForm.js
//
// Rider details — RIDER-HELD (Nigel decision 2026-07-22, adopting Claude
// Design's open question #1): staff tap "Check out — hand to rider" on the
// board card, then the RIDER types their own name + email here and continues
// straight into the waiver. Kills the #1 typo risk (staff mishearing an email
// at a loud rack). The device goes back to staff at the confirm screen.
//
// On continue, DemoFlow runs the repeat-rider waiver lookup — a rider with a
// waiver on file for this event + version skips straight to confirm (<15s path).

import React, { useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel, FBoardCard } from '../foam/FoamPrimitives';
import { isValidEmail } from '../../services/demoService';

const Field = ({ label, optional, ...props }) => (
  <div className="flex-1">
    <div className="flex justify-between mb-[7px]">
      <FLabel>{label}</FLabel>
      {optional && <FLabel color="var(--foam-mute)">Optional</FLabel>}
    </div>
    <input
      {...props}
      className="w-full font-sans text-[16px] px-[14px]"
      style={{ height: 56, border: '1.5px solid var(--foam-line)', borderRadius: 0, background: 'var(--foam-paper)' }}
    />
  </div>
);

export default function DemoRiderForm({ product, initial, checking, onContinue, onBack }) {
  const [firstName, setFirstName] = useState(initial?.firstName || '');
  const [lastName, setLastName] = useState(initial?.lastName || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [touched, setTouched] = useState(false);

  const emailOk = isValidEmail(email);
  const ready = firstName.trim().length > 0 && lastName.trim().length > 0 && emailOk && !checking;

  return (
    <FScreen
      header={
        <FHeader
          right={
            // Design r2 (DRiderForm): the hand-over moment gets an orange pill —
            // the one screen where the RIDER is holding the device.
            <span
              className="font-mono font-bold uppercase"
              style={{ fontSize: 10, letterSpacing: '0.08em', padding: '5px 9px',
                background: 'var(--foam-orange)', color: 'var(--foam-carbon)', whiteSpace: 'nowrap' }}
            >
              ● Your turn
            </span>
          }
        />
      }
      footer={
        <>
          <FBtn
            primary
            disabled={!ready}
            onClick={() => onContinue({ firstName, lastName, email, phone })}
          >
            {checking ? 'One sec…' : 'Next — sign the waiver →'}
          </FBtn>
          <FBtn ghost onClick={onBack}>Back</FBtn>
        </>
      }
    >
      <FBoardCard product={product} small />
      <FHeadline size={44}>Hey rider —<br /><FHeadline.Accent>who are you?</FHeadline.Accent></FHeadline>
      <p className="font-sans text-[14px]" style={{ color: 'var(--foam-ink-2)', lineHeight: 1.5 }}>
        You've got the phone now. Pop your details in, then sign — takes about a minute.
      </p>

      <div className="flex flex-col gap-[14px]">
        <div className="flex gap-[12px]">
          <Field label="First" value={firstName} autoComplete="given-name"
            onChange={(e) => setFirstName(e.target.value)} />
          <Field label="Last" value={lastName} autoComplete="family-name"
            onChange={(e) => setLastName(e.target.value)} />
        </div>
        <Field label="Email" type="email" inputMode="email" value={email} autoComplete="email"
          onChange={(e) => setEmail(e.target.value)} onBlur={() => setTouched(true)} />
        {touched && email && !emailOk && (
          <FLabel color="var(--foam-orange)">Check that email — it doesn't look right</FLabel>
        )}
        <Field label="Phone" optional type="tel" inputMode="tel" value={phone} autoComplete="tel"
          placeholder="+1 …" onChange={(e) => setPhone(e.target.value)} />
      </div>

      <p className="font-sans text-[12px]" style={{ color: 'var(--foam-ink-2)' }}>
        We'll send your ride summary here — the exact board you rode, and where to get one.
        No spam, and worth a spell-check.
      </p>
    </FScreen>
  );
}
